import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from "./components/Login";
import Profile from "./components/Profile";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { TokenManager } from "./utils/tokenManager";
import inactivityTimer from "./utils/inactivityTimer";
import "./style/global-theme.css";

import { ColorModeProvider } from "./contexts/ColorModeContext";
import MainLayout from "./components/layout/MainLayout";
import Dashboard from "./pages/Dashboard/Dashboard";
import CustomerDetailsPage from "./pages/Customers/CustomerDetailsPage";
//import InvoicesList from "./components/layout/InvoicesList";
import CreateInvoice from "./pages/Invoices/CreateInvoice";
import ViewInvoices from "./pages/Invoices/ViewInvoices";
import SalesOrder from "./pages/Sales/SalesOrder";
import SalesList from "./pages/Sales/SalesList";
import PurchaseOrderList from "./pages/Purchases/PurchaseOrderList";
import PurchaseOrderPage from "./pages/Purchases/PurchaseOrderPage";
import InventoryList from "./pages/Inventory/InventoryList";
import ItemForm from "./pages/Inventory/ItemForm";
import ItemDetails from "./pages/Inventory/ItemDetails";
import BankingList from "./pages/Banking/BankingList";
import Reports from "./pages/Reports/Reports";
import NotFoundPage from "./pages/NotFoundPage";
// import ReportsList from "./pages/Reports/ReportsList";
import ItemNotFound from "./pages/Inventory/ItemNotFound";
import DocumentsList from "./pages/Documents/DocumentsList";
import SettingsList from "./pages/Settings/SettingsList";
import ViewChallan from "./pages/Challans/ViewChallan";
import DeliveredChallan from "./pages/Challans/DeliveredChallan";
import ReceivedChallan from "./pages/Challans/ReceivedChallan";
import CombinedChallan from "./pages/Challans/CombinedChallan";
import EditChallan from "./pages/Challans/EditChallan";
import Goods from "./pages/Inventory/Goods";
import CylinderMaster from "./pages/Cylinders/CylinderMaster";
import CylinderHistory from "./pages/Cylinders/CylinderHistory";
import Passbook from "./pages/Customers/Passbook";
import VendorPassbook from "./pages/VendorPassbook/VendorPassbook";
import VendorProductDetails from "./pages/VendorPassbook/VendorProductDetails";
import CreateCustomer from "./pages/Customers/CreateCustomer";
import ProductDetails from "./pages/Customers/ProductDetails";
import UsersList from "./pages/Users/UsersList";
import EditUser from "./pages/Users/EditUser";
import BillingStatements from "./pages/Billing/BillingStatements";
import VendorForm from "./pages/vendor/vendorform";
import VendorTable from "./pages/vendor/VendorTable";
import CreditNote from "./pages/CreditNote/creditnote";
import DebitNote from "./pages/DebitNote/debitnote";
import CreditNoteList from "./pages/CreditNote/CreditNoteList";
import DebitNoteList from "./pages/DebitNote/DebitNoteList";
import PaymentsPage from "./pages/Payments/PaymentsPage";
import PaymentRecord from "./pages/Payments/paymentRecord";
import RecordPayment from "./pages/Payments/RecordPayment";
import RecurrenceList from "./pages/Invoices/RecurrenceList";
import RecurrencePage from "./pages/Invoices/Recurrencepage";
import BillsList from "./pages/Bill/BillsList";
import NewBill from "./pages/Bill/NewBill";
import EstimateQuotation from "./pages/Quotes/EstimateQuotation";
import EstimateQuotationList from "./pages/Quotes/EstimateQuotationList";
import ProformaInvoice from "./pages/Sales/ProformaInvoice";
import ProformaInvoiceList from "./pages/Sales/ProformaInvoiceList";
import CustomerPO from "./pages/Sales/CustomerPO";
import CustomerPOList from "./pages/Sales/CustomerPOList";
import CylinderDashboard from "./pages/Cylinders/CylinderDashboard";

// CRM Pages
import CRMDashboard from "./pages/CRM/CRMDashboard";
import CRMLeads from "./pages/CRM/CRMLeads";
import CRMMyLeads from "./pages/CRM/CRMMyLeads";
import CRMIndiaMartLeads from "./pages/CRM/CRMIndiaMartLeads";
import CRMCompanies from "./pages/CRM/CRMCompanies";
import CRMContacts from "./pages/CRM/CRMContacts";
import CRMFollowups from "./pages/CRM/CRMFollowups";
import CRMCalls from "./pages/CRM/CRMCalls";
import CRMWhatsApp from "./pages/CRM/CRMWhatsApp";
import CRMBulkMessages from "./pages/CRM/CRMBulkMessages";
import CRMMessages from "./pages/CRM/CRMMessages";
import CRMTemplates from "./pages/CRM/CRMTemplates";
import CRMPipeline from "./pages/CRM/CRMPipeline";
import CRMOpportunities from "./pages/CRM/CRMOpportunities";
import CRMActivities from "./pages/CRM/CRMActivities";
import CRMReports from "./pages/CRM/CRMReports";
import CRMLeadDetail from "./pages/CRM/CRMLeadDetail";

const isAuthenticated = () => TokenManager.isLoggedIn();

const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => {
  useEffect(() => {
    if (TokenManager.isLoggedIn()) {
      inactivityTimer.start();
    } else {
      inactivityTimer.stop();
    }

    const handleStorage = (event) => {
      if (event.key === "token" && event.newValue === null) {
        inactivityTimer.logout(false);
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
      inactivityTimer.stop();
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    let timeoutId;
    const handleResize = () => {
      root.classList.add("resizing");
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => root.classList.remove("resizing"), 150);
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(timeoutId);
      root.classList.remove("resizing");
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeProvider>
        <CssBaseline />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={true}
          draggable={true}
          pauseOnHover={true}
          theme="colored"
          closeButton={true}
          style={{
            zIndex: 9999,
            marginTop: '80px'
          }}
        />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" /></ProtectedRoute>} />

            <Route
              path="/login"
              element={!isAuthenticated() ? <Login /> : <Navigate to="/dashboard" />}
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="items/new" element={<ItemForm />} />
              <Route path="items/:id" element={<ItemDetails />} />
              <Route path="items/:id/edit" element={<ItemForm />} />
              <Route path="items/not-found" element={<ItemNotFound />} />
              <Route path="customers/:id" element={<CustomerDetailsPage />} />
              <Route path="customers/:id/edit" element={<CreateCustomer />} />
              <Route path="items" element={<InventoryList />} />
              <Route path="customers" element={<CustomerDetailsPage />} />
              <Route path="customers/new" element={<CreateCustomer />} />
              <Route path="customers/create" element={<Navigate to="/dashboard/customers/new" replace />} />
              <Route path="customers/passbook" element={<Passbook />} />
              <Route path="product-details/:customerId/:productName" element={<ProductDetails />} />
              <Route path="billing-statements" element={<BillingStatements />} />

              {/* Vendors Routes */}
              <Route path="vendors/form" element={<VendorForm />} />
              <Route path="vendors/view" element={<VendorTable />} />

              {/* Challans Routes */}
              <Route path="challans">
                <Route index element={<ViewChallan />} />
                <Route path="view" element={<ViewChallan />} />
                <Route path="delivered" element={<DeliveredChallan />} />
                <Route path="delivered/edit/:id" element={<DeliveredChallan />} />
                <Route path="received" element={<ReceivedChallan />} />
                <Route path="received/edit/:id" element={<ReceivedChallan />} />
                <Route path="combined" element={<CombinedChallan />} />
                <Route path="combined/edit/:id" element={<CombinedChallan />} />
                <Route path="create" element={<CombinedChallan />} />
                <Route path="edit/:id" element={<EditChallan />} />
              </Route>


              {/* Inventory Routes */}
              <Route path="inventory/goods" element={<Goods />} />
              <Route path="inventory/cylinders" element={<CylinderMaster />} />

              {/* Cylinders Routes */}
              <Route path="cylinders/history" element={<CylinderHistory />} />
              <Route path="cylinders/cylinderdashboard" element={<CylinderDashboard />}/>
            

              {/* Users Routes */}
              <Route path="users" element={<UsersList />} />
              <Route path="users/new" element={<UsersList />} />
              <Route path="edit-user/:id" element={<EditUser />} />
              <Route path="create-user" element={<Navigate to="/dashboard/users" replace />} />
              <Route path="view-users" element={<Navigate to="/dashboard/users" replace />} />

              {/* Sales Routes */}
                           {/* Sales Routes - FIXED */}
              <Route path="sales-orders">
                <Route index element={<SalesList />} />
                <Route path="new" element={<SalesOrder />} />
                <Route path="edit/:id" element={<SalesOrder />} />
              </Route>

              {/* Invoices Routes */}
              <Route path="invoices/view" element={<ViewInvoices />} />
              <Route path="invoices/create" element={<CreateInvoice />} />

              {/* Payments Routes */}
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="payments/new" element={<RecordPayment />} />
              <Route path="payments/legacy" element={<PaymentRecord />} />

              {/* Recurring Invoices Routes */}
              <Route path="recurring-list" element={<RecurrenceList />} />
              <Route path="recurring" element={<RecurrencePage />} />

              {/* Credit & Debit Notes Routes */}
              <Route path="credit-notes">
                <Route index element={<CreditNoteList />} />
                <Route path="new" element={<CreditNote />} />
                <Route path="edit/:id" element={<CreditNote />} />
              </Route>
              <Route path="debit-notes">
                <Route index element={<DebitNoteList />} />
                <Route path="new" element={<DebitNote />} />
                <Route path="edit/:id" element={<DebitNote />} />
              </Route>

              {/* Purchases Routes */}
              <Route path="purchases" element={<PurchaseOrderList />} />
              <Route path="purchases/orders" element={<PurchaseOrderList />} />
              <Route path="purchases/orders/new" element={<PurchaseOrderPage />} />
              <Route path="purchases/orders/edit/:id" element={<PurchaseOrderPage />} />
              <Route path="purchases/entry" element={<PurchaseOrderList />} />
              <Route path="purchases/inward" element={<PurchaseOrderPage />} />
              <Route path="purchases/inward/new" element={<PurchaseOrderPage />} />
              <Route path="purchases/inward/edit/:id" element={<PurchaseOrderPage />} />
              <Route path="purchases/outward" element={<PurchaseOrderPage />} />
              <Route path="purchases/outward/new" element={<PurchaseOrderPage />} />
              <Route path="purchases/outward/edit/:id" element={<PurchaseOrderPage />} />
              <Route path="purchases/vendor-passbook" element={<VendorPassbook />} />
              <Route path="purchases/vendor-passbook/product/:vendorId/:goodsItemId" element={<VendorProductDetails />} />

              {/* Bills Routes */}
              <Route path="bills" element={<BillsList />} />
              <Route path="new-bill" element={<NewBill />} />

              {/* Estimates Routes */}
              <Route path="estimates" element={<EstimateQuotationList />} />
              <Route path="estimates/new" element={<EstimateQuotation />} />

              {/* Proforma Invoice Routes */}
              <Route path="proforma-invoices" element={<ProformaInvoiceList />} />
              <Route path="proforma-invoices/new" element={<ProformaInvoice />} />

              {/* Customer PO Routes */}
              <Route path="customer-pos" element={<CustomerPOList />} />
              <Route path="customer-pos/new" element={<CustomerPO />} />

              {/* Legacy purchase-order routes used by the Purchase pages */}
              <Route path="purchase-orders" element={<PurchaseOrderList />} />
              <Route path="purchase-orders/new" element={<PurchaseOrderPage />} />
              <Route path="purchase-orders/edit/:id" element={<PurchaseOrderPage />} />

              {/* Banking Routes */}
              <Route path="banking" element={<BankingList />} />
              <Route path="banking/accounts" element={<BankingList />} />
              <Route path="banking/transactions" element={<BankingList />} />
              <Route path="banking/cash" element={<BankingList />} />
              <Route path="banking/reconciliation" element={<BankingList />} />

              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<SettingsList />} />
              <Route path="documents" element={<DocumentsList />} />
              <Route path="profile" element={<Profile />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route
              path="/crm"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<CRMDashboard />} />
              <Route path="leads" element={<CRMLeads />} />
              <Route path="leads/new" element={<CRMLeadDetail />} />
              <Route path="leads/:id" element={<CRMLeadDetail />} />
              <Route path="my-leads" element={<CRMMyLeads />} />
              <Route path="india-mart-leads" element={<CRMIndiaMartLeads />} />
              <Route path="companies" element={<CRMCompanies />} />
              <Route path="companies/new" element={<CRMCompanies />} />
              <Route path="companies/:id" element={<CRMCompanies />} />
              <Route path="contacts" element={<CRMContacts />} />
              <Route path="contacts/new" element={<CRMContacts />} />
              <Route path="contacts/:id" element={<CRMContacts />} />
              <Route path="followups" element={<CRMFollowups />} />
              <Route path="calls" element={<CRMCalls />} />
              <Route path="whatsapp" element={<CRMWhatsApp />} />
              <Route path="bulk-messages" element={<CRMBulkMessages />} />
              <Route path="marketing-campaigns" element={<CRMMessages />} />
              <Route path="templates" element={<CRMTemplates />} />
              <Route path="pipeline" element={<CRMPipeline />} />
              <Route path="opportunities" element={<CRMOpportunities />} />
              <Route path="opportunities/new" element={<CRMOpportunities />} />
              <Route path="opportunities/:id" element={<CRMOpportunities />} />
              <Route path="activities" element={<CRMActivities />} />
              <Route path="reports" element={<CRMReports />} />
            </Route>

          </Routes>
        </BrowserRouter>
      </ColorModeProvider>
    </QueryClientProvider>
  );
};

export default App;
